export const EXPRESSION_EVENTS = {
  insertComposer: "whisprai:insert-composer",
  sendGifByKeyword: "whisprai:send-gif-by-keyword",
  sendGifByUrl: "whisprai:send-gif-by-url",
  sendVoiceLine: "whisprai:send-voice-line",
  sendVoicedGif: "whisprai:send-voiced-gif",
  sendVoicedSticker: "whisprai:send-voiced-sticker",
  openStickerVoiceOverride: "whisprai:open-sticker-voice-override",
  openChangeVoice: "whisprai:open-change-voice",
  openAiGifRegenerate: "whisprai:open-ai-gif-regenerate",
  jumpToAiGif: "whisprai:jump-to-ai-gif",
  toggleGifVoice: "whisprai:gif-toggle-voice",
  stickerCreateStarted: "whisprai:sticker-create-started",
  stickerCreated: "whisprai:sticker-created",
  stickerCreateFailed: "whisprai:sticker-create-failed",
} as const;

export interface ExpressionGif {
  url: string;
  previewUrl: string;
  width: number;
  height: number;
}

export interface ExpressionEventMap {
  [EXPRESSION_EVENTS.insertComposer]: {
    text: string;
    conversationId?: string;
    mode?: "replace" | "append";
  };
  [EXPRESSION_EVENTS.sendGifByKeyword]: {
    keyword: string;
    conversationId?: string;
    caption?: string;
  };
  [EXPRESSION_EVENTS.sendGifByUrl]: {
    gif: ExpressionGif;
    conversationId?: string;
    caption?: string;
    aiMeta?: Record<string, unknown>;
  };
  [EXPRESSION_EVENTS.sendVoiceLine]: {
    text: string;
    conversationId?: string;
    voiceId?: string;
  };
  [EXPRESSION_EVENTS.sendVoicedGif]: {
    gif: ExpressionGif;
    audioUrl: string;
    text: string;
    conversationId?: string;
    aiMeta?: Record<string, unknown>;
  };
  [EXPRESSION_EVENTS.sendVoicedSticker]: {
    stickerUrl: string;
    audioUrl: string;
    transcript?: string;
    tags?: string[];
    conversationId?: string;
  };
  [EXPRESSION_EVENTS.openStickerVoiceOverride]: {
    stickerUrl: string;
    tags?: string[];
    conversationId?: string;
  };
  [EXPRESSION_EVENTS.openChangeVoice]: {
    text?: string;
    conversationId?: string;
  };
  [EXPRESSION_EVENTS.openAiGifRegenerate]: {
    conversationId?: string;
    prompt?: string;
    voiceText?: string;
    voiceId?: string | null;
  };
  [EXPRESSION_EVENTS.jumpToAiGif]: {
    conversationId?: string;
    prompt?: string;
    voiceText?: string;
    voiceId?: string | null;
  };
  [EXPRESSION_EVENTS.toggleGifVoice]: {
    messageId?: string;
  };
  [EXPRESSION_EVENTS.stickerCreateStarted]: {
    requestId: string;
  };
  [EXPRESSION_EVENTS.stickerCreated]: {
    sticker: unknown;
    requestId: string;
  };
  [EXPRESSION_EVENTS.stickerCreateFailed]: {
    error: string;
    code?: string;
    requestId: string;
  };
}

type ExpressionEventName = keyof ExpressionEventMap;

export function emitExpressionEvent<K extends ExpressionEventName>(
  name: K,
  detail: ExpressionEventMap[K],
) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export function listenExpressionEvent<K extends ExpressionEventName>(
  name: K,
  handler: (detail: ExpressionEventMap[K], event: CustomEvent<ExpressionEventMap[K]>) => void,
) {
  const listener = (event: Event) => {
    const customEvent = event as CustomEvent<ExpressionEventMap[K]>;
    handler(customEvent.detail, customEvent);
  };
  window.addEventListener(name, listener as EventListener);
  return () => window.removeEventListener(name, listener as EventListener);
}

export const expressionActions = {
  insertComposer: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.insertComposer]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.insertComposer, detail),
  sendGifByKeyword: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.sendGifByKeyword]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.sendGifByKeyword, detail),
  sendGifByUrl: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.sendGifByUrl]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.sendGifByUrl, detail),
  sendVoiceLine: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.sendVoiceLine]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.sendVoiceLine, detail),
  sendVoicedGif: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.sendVoicedGif]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.sendVoicedGif, detail),
  sendVoicedSticker: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.sendVoicedSticker]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.sendVoicedSticker, detail),
  openStickerVoiceOverride: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.openStickerVoiceOverride]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.openStickerVoiceOverride, detail),
  openChangeVoice: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.openChangeVoice]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.openChangeVoice, detail),
  openAiGifRegenerate: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.openAiGifRegenerate]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.openAiGifRegenerate, detail),
  jumpToAiGif: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.jumpToAiGif]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.jumpToAiGif, detail),
  toggleGifVoice: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.toggleGifVoice]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.toggleGifVoice, detail),
  stickerCreateStarted: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.stickerCreateStarted]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.stickerCreateStarted, detail),
  stickerCreated: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.stickerCreated]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.stickerCreated, detail),
  stickerCreateFailed: (detail: ExpressionEventMap[typeof EXPRESSION_EVENTS.stickerCreateFailed]) =>
    emitExpressionEvent(EXPRESSION_EVENTS.stickerCreateFailed, detail),
};
